import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Popover } from 'antd';
import { COMPACT_LINK_STYLE, FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, RADIUS, SHADOW, SPACING } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { AppButton } from './AppButton';

/** 제외 사유 한 갈래 — 같은 사유는 한 줄로 묶어 건수만 센다(10-29 문서 C5 의 다섯 문구를 한 틀로). */
export interface BulkExcludeGroup {
  label: string;
  count: number;
}

/** 범위 선택 버튼 하나 — "이 페이지 N개" · "검색 결과 전체 N개". 고를 수 있는 것이 0 이면 버튼을 그리지 않는다. */
export interface BulkRange {
  count: number;
  /** 이 범위가 이미 전부 골라졌는가 — 그러면 그 버튼은 그리지 않는다(해제는 '선택 해제' 하나로). */
  allSelected: boolean;
  onSelect: () => void;
}

export interface BulkActionBarProps {
  /** 고른 수(제외 포함). 0 이면 바를 그리지 않는다(결정 D5 — 선택 1건 이상일 때만). */
  selectedCount: number;
  /** 고른 것 중 실제로 보낼 · 반영할 수. */
  applicableCount: number;
  /** 고른 것 중 빠지는 것 — 사유별 건수. */
  excluded?: BulkExcludeGroup[];
  /** 지금 페이지 범위. 표에 페이지가 없으면 넘기지 않는다. */
  page?: BulkRange;
  /** 검색 · 필터 결과 전체 범위 — 바 왼쪽 체크박스(일부 선택이면 indeterminate)도 이 범위를 따른다. */
  all?: BulkRange;
  onClear: () => void;
  /** 오른쪽 실행 버튼(들). 같은 바에 'PriceHub 에 요청' 과 '네이버에 반영' 을 같이 두지 않는다(규칙 22 · 안 A). */
  actions?: React.ReactNode;
  /** 선택 수 앞에 쓸 머리 글(예: '✓ 하락 확인 완료'). */
  lead?: React.ReactNode;
  /** 바 아래 줄(통계 · 진행 글). */
  children?: React.ReactNode;
  tone?: 'info' | 'success';
  /** 넓은 화면에서 화면 위(헤더 아래)에 붙인다. 휴대폰 폭은 늘 화면 아래에 고정한다. */
  sticky?: boolean;
  phone?: boolean;
  /** 선택이 0 이어도 그린다 — 보내는 중 진행 글처럼 선택과 무관하게 보여야 할 때. */
  forceVisible?: boolean;
  /** 바 높이(px) — 표 머리줄을 바로 아래 붙이는 데 쓴다. 숨으면 0. */
  onHeightChange?: (h: number) => void;
  ariaLabel?: string;
  /** 바 바깥 여백(표 위에 놓일 때 아래 간격 등). */
  style?: React.CSSProperties;
}

/** 휴대폰 폭에서 화면 아래 고정 바의 높이 — 페이지 끝 여백(App.css `.dashboard-main::after`)이 읽는다. */
export const BULK_BAR_SPACE_VAR = '--bulk-bar-space';

/** 제외 사유를 바에 한 줄로 — 둘까지 쓰고 나머지는 '외 N'. 전부는 '사유 보기' 에. */
export function excludeSummary(groups: BulkExcludeGroup[]): string {
  const live = groups.filter((g) => g.count > 0);
  if (live.length === 0) return '';
  const head = live.slice(0, 2).map((g) => `${g.label} ${g.count.toLocaleString()}`);
  const rest = live.length - head.length;
  return rest > 0 ? `${head.join(' · ')} 외 ${rest}` : head.join(' · ');
}

/** 같은 사유끼리 묶는다(순서는 처음 나온 순). */
export function groupExcluded(labels: Array<string | null | undefined>): BulkExcludeGroup[] {
  const m = new Map<string, number>();
  labels.forEach((l) => { if (l) m.set(l, (m.get(l) ?? 0) + 1); });
  return Array.from(m, ([label, count]) => ({ label, count }));
}

/**
 * 일괄 작업 바(가격 목록 개편 PR 2, 감사 card-controltower#696) — 가격 조회 · 매장 가격 요청이 같은 문구 틀 하나를 쓴다:
 *   ☐ `N개 선택 · 적용 가능 A · 제외 B (사유별)` [사유 보기]  [이 페이지 N개 선택] [검색 결과 전체 N개 선택] [선택 해제]   [실행]
 * 평소엔 숨고 1건 이상 고르면 나타난다(결정 D5). 넓은 화면은 표 위에 붙고(sticky), 휴대폰 폭은 화면 아래에 고정한다(누르는 곳 44px).
 * 왼쪽 체크박스는 '검색 결과 전체' 범위 — 일부만 골랐으면 indeterminate, 누르면 해제(전부 · 일부) 또는 전체 선택(0).
 * 실행 버튼은 바로 보내지 않고 미리보기(BulkPreviewModal 또는 ③ 반영 요약)를 거친다(결정 D6) — 그 흐름은 쓰는 쪽이 정한다.
 */
export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount, applicableCount, excluded = [], page, all, onClear, actions, lead, children,
  tone = 'info', sticky = false, phone = false, forceVisible = false, onHeightChange, ariaLabel = '선택 작업', style,
}) => {
  const t = useThemeTokens();
  const visible = selectedCount > 0 || forceVisible;
  const ref = useRef<HTMLDivElement>(null);
  const [h, setH] = useState(0);
  const heightCb = useRef(onHeightChange);
  heightCb.current = onHeightChange;

  useEffect(() => {
    const el = ref.current;
    if (!visible || !el) {
      setH(0);
      heightCb.current?.(0);
      return undefined;
    }
    const measure = () => {
      const next = Math.round(el.getBoundingClientRect().height);
      setH(next);
      heightCb.current?.(next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [visible, phone]);
  // 바가 통째로 빠질 때 부모의 높이 값을 0 으로 돌린다.
  useEffect(() => () => heightCb.current?.(0), []);
  // 휴대폰 폭은 바가 화면 아래에 떠 있다 — 페이지 맨 끝(표 · 아래 페이지 넘김 뒤)에 바 높이만큼 여백을 둔다. App.css 의
  // `.dashboard-main::after` 가 이 값을 읽는다(코덱 리뷰 #716 — 예전엔 여백 칸이 바 바로 뒤, 표 앞에 들어가 끝까지 내려도 페이지
  // 넘김이 바에 가렸다).
  useEffect(() => {
    if (!phone || !visible || h === 0) return undefined;
    const root = document.documentElement;
    root.style.setProperty(BULK_BAR_SPACE_VAR, `${h}px`);
    return () => { root.style.removeProperty(BULK_BAR_SPACE_VAR); };
  }, [phone, visible, h]);

  if (!visible) return null;

  const reasons = excluded.filter((g) => g.count > 0);
  const excludedCount = reasons.reduce((n, g) => n + g.count, 0);
  const summary = excludeSummary(reasons);
  const success = tone === 'success';
  const edge = success ? t.success : t.accent;
  const line = success ? t.lineSuccess : t.lineAccent;
  const link: React.CSSProperties = phone ? { ...COMPACT_LINK_STYLE, minHeight: 44 } : COMPACT_LINK_STYLE;

  const allChecked = !!all && all.count > 0 && all.allSelected;
  const someChecked = selectedCount > 0 && !allChecked;
  const toggleAll = () => {
    if (!all) return;
    if (allChecked || someChecked) onClear(); else all.onSelect();
  };

  const position: React.CSSProperties = phone
    ? { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20 }
    : sticky ? { position: 'sticky', top: 'var(--app-sticky-top, 56px)', zIndex: 5 } : {};

  return (
    <>
      <div
        ref={ref}
        role="group"
        aria-label={ariaLabel}
        data-testid="bulk-action-bar"
        data-sticky-bar=""
        style={{
          display: 'flex', flexDirection: 'column', gap: SPACING.xxs,
          padding: `${SPACING.xs}px ${SPACING.md}px`,
          background: success ? t.tintSuccess : t.tintInfo,
          border: `1px solid ${line}`, borderBottom: `2px solid ${edge}`,
          borderRadius: phone ? undefined : RADIUS.lg, boxShadow: SHADOW.card,
          ...style,
          ...position,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap', minWidth: 0 }}>
          {all && all.count > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              ...(phone ? { minWidth: 44, minHeight: 44 } : {}),
            }}>
              <Checkbox
                aria-label={allChecked || someChecked ? '선택 해제' : `검색 결과 전체 ${all.count.toLocaleString()}개 선택`}
                checked={allChecked}
                indeterminate={someChecked}
                onChange={toggleAll}
              />
            </span>
          )}
          <span
            data-testid="bulk-summary"
            aria-live="polite"
            style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary, lineHeight: LINE_HEIGHT.normal, minWidth: 0 }}
          >
            {lead && <span style={{ fontWeight: FONT_WEIGHT.semibold, marginRight: SPACING.xxs }}>{lead}</span>}
            <b style={{ fontWeight: FONT_WEIGHT.semibold, fontVariantNumeric: 'tabular-nums' }}>
              {selectedCount.toLocaleString()}개 선택
            </b>
            <span style={{ color: t.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
              {` · 적용 가능 ${applicableCount.toLocaleString()}`}
              {excludedCount > 0 && ` · 제외 ${excludedCount.toLocaleString()}${summary ? ` (${summary})` : ''}`}
            </span>
          </span>
          {reasons.length > 0 && (
            <Popover
              trigger="click"
              title={`제외 ${excludedCount.toLocaleString()}개 — 사유`}
              content={(
                <ul data-testid="bulk-exclude-reasons" style={{
                  margin: 0, paddingLeft: SPACING.md, fontSize: FONT_SIZE.sm, lineHeight: LINE_HEIGHT.relaxed,
                }}>
                  {reasons.map((g) => (
                    <li key={g.label}>
                      {g.label} <b style={{ fontVariantNumeric: 'tabular-nums' }}>{g.count.toLocaleString()}</b>
                    </li>
                  ))}
                </ul>
              )}
            >
              <AppButton variant="link" style={link}>사유 보기</AppButton>
            </Popover>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap' }}>
            {page && page.count > 0 && !page.allSelected && (
              <AppButton variant="link" style={link} onClick={page.onSelect}>
                이 페이지 {page.count.toLocaleString()}개 선택
              </AppButton>
            )}
            {all && all.count > 0 && !all.allSelected && (
              <AppButton variant="link" style={link} onClick={all.onSelect}>
                검색 결과 전체 {all.count.toLocaleString()}개 선택
              </AppButton>
            )}
            {selectedCount > 0 && (
              <AppButton variant="link" style={link} onClick={onClear}>선택 해제</AppButton>
            )}
          </span>
          {actions && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: SPACING.xs, marginLeft: 'auto', flexWrap: 'wrap' }}>
              {actions}
            </span>
          )}
        </div>
        {children}
      </div>
    </>
  );
};
